import { marked } from "marked";
import { Enums } from "../helper/enums";
import {
  _fullName,
  getTechnicianFirstName,
  getTechnicianLastName,
} from "./technician-entity-service";
import { customStatusEntity } from "../model/entity/custom-status-entity";
import { contactEntity } from "../model/entity/contact-entity";
import {
  isTicketReadByClient,
  markClientTicketRead,
  ticketActivityMsForClientRead,
} from "../helper/ticket-client-read-state";
import {
  isContactOriginatedNonMessagesTabActivity,
  isInboundTechnicianNonMessagesActivity,
  isNonMessagesTabActivityForClientUnread,
  resolveUnreadContextRecentComment,
} from "../helper/ticket-recent-comment-preview";

export interface TicketRecentCommentSnapshot {
  commentId?: string;
  ticketId?: string;
  comment?: string | null;
  markdownComment?: string | null;
  markdownWithoutSignature?: string | null;
  type?: string;
  isInternal?: boolean;
  createdOn?: string;
  createdByContactId?: number | null;
  createdByTechnicianId?: number | null;
  ruleId?: string | null;
  ruleName?: string | null;
  tick?: number;
  /** When false, optimistic / failed portal post — must not satisfy “has body” checks. */
  isSuccess?: boolean;
}

/** True when a cx/recent snapshot is delivered and has plain or markdown body text. */
export function hasRecentCommentDeliveredBody(
  snapshot: TicketRecentCommentSnapshot | null | undefined,
): boolean {
  if (snapshot == null) {
    return false;
  }
  if (snapshot.isSuccess === false) {
    return false;
  }
  const text = snapshot.comment != null ? String(snapshot.comment).trim() : "";
  const md =
    snapshot.markdownComment != null
      ? String(snapshot.markdownComment).trim()
      : "";
  const mdWithoutSignature =
    snapshot.markdownWithoutSignature != null
      ? String(snapshot.markdownWithoutSignature).trim()
      : "";
  return text !== "" || md !== "" || mdWithoutSignature !== "";
}

function isNonEmptyRuleId(ruleId?: string | null): boolean {
  return ruleId != null && ruleId !== "" && ruleId !== "null";
}

/** Automation/rule-engine posts are identified by `ruleId` (list payloads often omit `ruleName`). */
export function isAutomationRuleComment(
  ruleId?: string | null,
  _ruleName?: string | null,
): boolean {
  return isNonEmptyRuleId(ruleId);
}

export function _messageAuthorDisplayName(
  createdByTechnicianId: number | null | undefined,
  createdByContactId: number | null | undefined,
): string {
  const techId = Number(createdByTechnicianId ?? 0);
  const contactId = Number(createdByContactId ?? 0);
  if (techId > 0) {
    const name = _fullName(techId);
    if (name && String(name).trim()) {
      return String(name).trim();
    }
  }
  if (contactId > 0) {
    const contact = contactEntity.query().whereId(contactId).first();
    if (contact) {
      return `${contact.firstName ?? ""} ${contact.lastName ?? ""}`.trim();
    }
  }
  return "";
}

/** Sidebar / list author label; includes automation when posted by a rule. */
export function messageAuthorDisplayNameForComment(
  createdByTechnicianId?: number | null,
  createdByContactId?: number | null,
  ruleId?: string | null,
  ruleName?: string | null,
): string {
  if (isAutomationRuleComment(ruleId, ruleName)) {
    return "Automation";
  }
  return _messageAuthorDisplayName(createdByTechnicianId, createdByContactId);
}

export function _truncatedCommentPreview(
  comment: string | null | undefined,
  maxLen = 80,
): string {
  if (comment == null || comment === "") {
    return "";
  }
  const plain = String(comment)
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (plain.length <= maxLen) {
    return plain;
  }
  return plain.slice(0, maxLen) + "\u2026";
}

function sanitizeCommentMarkdown(markdownComment: string): string {
  return markdownComment
    .replace(/^(\|([^\S\r\n])*){1,}$/gm, "")
    .replace(/^>\s*$/gm, "");
}

export function _convertMarkdownToHTMLForComments(
  markdownComment: string,
): string {
  return marked.parse(sanitizeCommentMarkdown(markdownComment));
}

export function _contactName(
  createdByTechnicianId: number,
  createdByContactId: number,
) {
  return _messageAuthorDisplayName(createdByTechnicianId, createdByContactId);
}

export function _contactFirstName(
  createdByTechnicianId: number,
  createdByContactId: number,
) {
  if (createdByTechnicianId != null && createdByTechnicianId != 0) {
    return getTechnicianFirstName(createdByTechnicianId);
  }
}

export function _contactLastName(
  createdByTechnicianId: number,
  createdByContactId: number,
) {
  if (createdByTechnicianId != null && createdByTechnicianId != 0) {
    return getTechnicianLastName(createdByTechnicianId);
  }
}

export function _isTicketNotViewed(lastActionId: number) {
  if (lastActionId && lastActionId != 0) {
    switch (lastActionId) {
      case Enums.TicketLastAction.StatusUpdated:
      case Enums.TicketLastAction.Read:
        return false;

      case Enums.TicketLastAction.New:
        return true;

      case Enums.TicketLastAction.CommentCreated:
        return false;

      case Enums.TicketLastAction.ExternalCommentCreated:
      case Enums.TicketLastAction.Unread:
        return true;

      default:
        return false;
    }
  } else {
    return false;
  }
}

function parseLastModifiedJson(
  lastModifiedJson?: string | null,
): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(lastModifiedJson ?? "{}");
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export type ClientPortalUnreadContext = {
  viewerContactId?: number;
  recentComment?: TicketRecentCommentSnapshot | null;
  recentCommentId?: string | null;
  createdByTechnicianId?: number | null;
  createdByContactId?: number | null;
  /** Primary portal contact on the ticket when `createdByContactId` is missing from API. */
  ticketContactId?: number | null;
};

function isLatestCommentOutsideMessagesRecent(
  lastActionId: number,
  context?: ClientPortalUnreadContext,
): boolean {
  if (lastActionId !== Enums.TicketLastAction.CommentCreated) {
    return false;
  }
  const latestCommentId = String(context?.recentCommentId ?? "").trim();
  if (latestCommentId === "") {
    return false;
  }
  const messagesRecentCommentId = String(
    context?.recentComment?.commentId ?? "",
  ).trim();
  return (
    messagesRecentCommentId === "" ||
    messagesRecentCommentId !== latestCommentId
  );
}

/** Portal contact who opened the ticket (creator id, else ticket contact). */
export function resolvePortalTicketCreatorContactId(
  context?: ClientPortalUnreadContext,
): number {
  const created = Number(context?.createdByContactId ?? 0);
  if (created > 0) {
    return created;
  }
  return Number(context?.ticketContactId ?? 0);
}

/** System comment recorded when a portal contact submits a form on a new ticket. */
export function isPortalFormSubmissionEchoComment(
  comment: string | null | undefined,
): boolean {
  if (comment == null || String(comment).trim() === "") {
    return false;
  }
  const plain = String(comment)
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return (
    plain === "Form Response Added" || plain.includes("Form Response Added")
  );
}

function plainTextFromCommentOrMarkdown(
  commentHtml: string | null | undefined,
  markdownComment: string | null | undefined,
): string {
  if (commentHtml != null && String(commentHtml).trim() !== "") {
    return String(commentHtml)
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  if (markdownComment == null || String(markdownComment).trim() === "") {
    return "";
  }
  const html = marked.parse(
    sanitizeCommentMarkdown(String(markdownComment)),
  ) as string;
  return String(html)
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Latest activity was initiated by an MSP technician (not the portal contact). */
export function isTechnicianOriginatedTicketActivity(
  lastModifiedJson?: string | null,
  context?: ClientPortalUnreadContext,
): boolean {
  const recent = context?.recentComment;
  if (recent != null) {
    if (Number(recent.createdByTechnicianId ?? 0) > 0) {
      return true;
    }
    if (isAutomationRuleComment(recent.ruleId, recent.ruleName)) {
      return true;
    }
    if (Number(recent.createdByContactId ?? 0) > 0) {
      return false;
    }
  }

  const lastModifiedObj = parseLastModifiedJson(lastModifiedJson);
  if (lastModifiedObj?.byAutomation === true) {
    return true;
  }
  if (lastModifiedObj?.byTech === true) {
    return true;
  }

  if (Number(context?.createdByTechnicianId ?? 0) > 0) {
    return true;
  }

  return false;
}

/** True when the latest activity was posted by the logged-in portal contact (not a technician). */
export function isUnreadActivityFromLoggedInContact(
  lastModifiedJson?: string | null,
  recentComment?: TicketRecentCommentSnapshot | null,
  viewerContactId?: number,
): boolean {
  const viewerId = Number(viewerContactId ?? 0);
  if (viewerId <= 0) {
    return false;
  }

  if (
    isContactOriginatedNonMessagesTabActivity(
      lastModifiedJson,
      recentComment,
      viewerContactId,
    )
  ) {
    return false;
  }

  if (recentComment) {
    if (isAutomationRuleComment(recentComment.ruleId, recentComment.ruleName)) {
      return false;
    }
    const techId = Number(recentComment.createdByTechnicianId ?? 0);
    if (techId > 0) {
      return false;
    }
    const contactId = Number(recentComment.createdByContactId ?? 0);
    if (contactId > 0 && contactId === viewerId) {
      return true;
    }
  }

  const lastModifiedObj = parseLastModifiedJson(lastModifiedJson);
  if (lastModifiedObj?.byAutomation === true) {
    return false;
  }
  if (lastModifiedObj && lastModifiedObj.byTech !== true) {
    const by = Number(lastModifiedObj.by ?? 0);
    if (by > 0 && by === viewerId) {
      return true;
    }
  }

  return false;
}

/** Client portal: mark unread when another thread gets an inbound message and user is not viewing it. */
export function shouldMarkTicketUnreadForClient(
  lastActionId: number,
  ticketId: string,
  selectedTicketId: string | null | undefined,
  lastModifiedJson?: string | null,
  context?: ClientPortalUnreadContext,
  activityMsForRead = 0,
): boolean {
  if (!ticketId || String(ticketId) === String(selectedTicketId ?? "")) {
    return false;
  }

  const viewerId = Number(context?.viewerContactId ?? 0);
  if (
    viewerId > 0 &&
    isTicketReadByClient(viewerId, ticketId, activityMsForRead)
  ) {
    return false;
  }

  if (
    isUnreadActivityFromLoggedInContact(
      lastModifiedJson,
      context?.recentComment,
      context?.viewerContactId,
    )
  ) {
    return false;
  }

  if (lastActionId === Enums.TicketLastAction.StatusUpdated) {
    return false;
  }

  const lastModifiedObj = parseLastModifiedJson(lastModifiedJson);
  // Status-only edits (editedType 8) must not surface as unread in the message list.
  if (lastModifiedObj?.editedType === 8) {
    return false;
  }

  if (
    isInboundTechnicianNonMessagesActivity(
      lastModifiedJson,
      context?.recentComment,
      context?.viewerContactId,
    )
  ) {
    return true;
  }

  if (
    isContactOriginatedNonMessagesTabActivity(
      lastModifiedJson,
      context?.recentComment,
      context?.viewerContactId,
    )
  ) {
    return false;
  }

  // The ticket row may point at a private/TPC/Approval comment while cx/recent
  // still points at the latest Messages-tab comment. Hidden tab activity must
  // not create a fresh unread badge for the Messages list.
  if (isLatestCommentOutsideMessagesRecent(lastActionId, context)) {
    return false;
  }

  if (lastActionId === Enums.TicketLastAction.New) {
    const viewerId = Number(context?.viewerContactId ?? 0);
    // Only the portal contact who created the ticket suppresses unread — not
    // contacts who are only mapped via ticket `contactId` (e.g. web-created by MSP).
    const createdByContact = Number(context?.createdByContactId ?? 0);
    if (viewerId > 0 && createdByContact > 0 && createdByContact === viewerId) {
      return false;
    }
    return true;
  }

  if (
    lastActionId === Enums.TicketLastAction.CommentCreated &&
    isTechnicianOriginatedTicketActivity(lastModifiedJson, context)
  ) {
    const viewerId = Number(context?.viewerContactId ?? 0);
    const creatorContactId = resolvePortalTicketCreatorContactId(context);
    if (viewerId > 0 && creatorContactId > 0 && creatorContactId === viewerId) {
      const lm = parseLastModifiedJson(lastModifiedJson);
      const commentText =
        context?.recentComment?.comment ??
        (lm?.comment != null ? String(lm.comment) : null);
      const markdownText = context?.recentComment?.markdownComment ?? null;
      if (
        isPortalFormSubmissionEchoComment(commentText) ||
        isPortalFormSubmissionEchoComment(
          plainTextFromCommentOrMarkdown(null, markdownText),
        )
      ) {
        return false;
      }
    }
    return true;
  }

  if (
    lastActionId === Enums.TicketLastAction.CommentCreated &&
    context?.recentComment?.isInternal !== true &&
    hasRecentCommentDeliveredBody(context?.recentComment)
  ) {
    const lm = parseLastModifiedJson(lastModifiedJson);
    const commentText =
      context?.recentComment?.comment ??
      (lm?.comment != null ? String(lm.comment) : null);
    const markdownText = context?.recentComment?.markdownComment ?? null;
    return (
      !isPortalFormSubmissionEchoComment(commentText) &&
      !isPortalFormSubmissionEchoComment(
        plainTextFromCommentOrMarkdown(null, markdownText),
      )
    );
  }

  const recentForDelivered = context?.recentComment as
    | { isSuccess?: boolean }
    | undefined;
  if (
    lastActionId === Enums.TicketLastAction.CommentCreated &&
    !(recentForDelivered != null && recentForDelivered.isSuccess === false) &&
    !isTechnicianOriginatedTicketActivity(lastModifiedJson, context)
  ) {
    const authorContactId = Number(
      context?.recentComment?.createdByContactId ?? 0,
    );
    if (authorContactId > 0 && authorContactId !== viewerId) {
      return true;
    }
  }

  if (_isTicketNotViewed(lastActionId)) {
    return true;
  }

  return false;
}

/** Apply unread flag when merging ticket payloads from SignalR / tick sync (never trust API isUnread alone). */
export function applyClientPortalUnreadStateOnTicketSync(
  ticket: {
    ticketId?: string;
    lastActionId?: number;
    lastModifiedJson?: string | null;
    recentComment?: TicketRecentCommentSnapshot | null;
    createdByTechnicianId?: number | null;
    createdByContactId?: number | null;
    createdOn?: string | null;
    jsCreatedOn?: Date | null;
    updatedOn?: string | null;
    isUnread?: boolean;
  },
  selectedTicketId: string | null | undefined,
  previousIsUnread?: boolean,
  viewerContactId?: number,
): void {
  const ticketId = String(ticket.ticketId ?? "");
  const activityMs = ticketActivityMsForClientRead(ticket);
  const unreadContext: ClientPortalUnreadContext = {
    viewerContactId,
    recentComment: resolveUnreadContextRecentComment(ticket),
    recentCommentId: (ticket as { recentCommentId?: string | null })
      .recentCommentId,
    createdByTechnicianId: ticket.createdByTechnicianId,
    createdByContactId: ticket.createdByContactId,
    ticketContactId: (ticket as { contactId?: number }).contactId,
  };

  if (
    isUnreadActivityFromLoggedInContact(
      ticket.lastModifiedJson,
      unreadContext.recentComment,
      viewerContactId,
    )
  ) {
    ticket.isUnread = false;
    return;
  }

  if (
    isContactOriginatedNonMessagesTabActivity(
      ticket.lastModifiedJson,
      unreadContext.recentComment,
      viewerContactId,
    )
  ) {
    if (previousIsUnread === true) {
      ticket.isUnread = true;
    }
    return;
  }

  if (
    isLatestCommentOutsideMessagesRecent(
      Number(ticket.lastActionId ?? 0),
      unreadContext,
    )
  ) {
    ticket.isUnread = previousIsUnread === true;
    return;
  }

  if (
    !isNonMessagesTabActivityForClientUnread(
      ticket.lastModifiedJson,
      unreadContext.recentComment,
    ) &&
    Number(viewerContactId ?? 0) > 0 &&
    isTicketReadByClient(Number(viewerContactId), ticketId, activityMs)
  ) {
    ticket.isUnread = false;
    return;
  }

  if (ticketId && ticketId === String(selectedTicketId ?? "")) {
    ticket.isUnread = false;
    return;
  }

  if (
    shouldMarkTicketUnreadForClient(
      Number(ticket.lastActionId ?? 0),
      ticketId,
      selectedTicketId,
      ticket.lastModifiedJson,
      unreadContext,
      activityMs,
    )
  ) {
    ticket.isUnread = true;
    return;
  }

  ticket.isUnread = previousIsUnread === true ? true : ticket.isUnread === true;
}

/**
 * After the logged-in contact submits a portal form (new ticket), keep sidebar unread off
 * and persist read state so SignalR/tick sync does not bump the badge.
 */
export function finalizePortalSelfSubmittedTicketInSidebar(
  ticket: Record<string, unknown>,
  viewerContactId: number,
  selectedTicketId?: string | null,
): Record<string, unknown> {
  const row = { ...ticket };
  const viewerId = Number(viewerContactId ?? 0);
  if (viewerId > 0) {
    if (!Number(row.createdByContactId ?? 0)) {
      row.createdByContactId = viewerId;
    }
    if (!Number(row.contactId ?? 0)) {
      row.contactId = viewerId;
    }
  }
  applyClientPortalUnreadStateOnTicketSync(
    row,
    selectedTicketId,
    false,
    viewerContactId,
  );
  row.isUnread = false;
  const ticketId = String(row.ticketId ?? "");
  if (viewerId > 0 && ticketId) {
    markClientTicketRead(
      viewerId,
      ticketId,
      ticketActivityMsForClientRead(row),
    );
  }
  return row;
}

export function _statusColor(ticketStatusId: number) {
  let ticketStatus = customStatusEntity.find(ticketStatusId) as any;
  if (
    ticketStatus &&
    ticketStatus.baseStatusId != ticketStatus.ticketStatusId
  ) {
    ticketStatusId = ticketStatus.baseStatusId;
  }
  switch (ticketStatusId) {
    case 1:
      return "error";
    case 2:
      return "warningDark";
    case 3:
      return "success";
    case 4:
      return "secondaryLight";
    case 5:
      return "secondaryLight";
    case 6:
      return "info";
    case 7:
      return "secondary";

    default:
      return "";
  }
}

function ticketTitleWithOptionalNumberPrefix(
  ticketNumberWithPrefix: any,
  ticketTitle: string,
  separator: string,
): string {
  let title = "";
  if (ticketNumberWithPrefix != null && ticketNumberWithPrefix != "") {
    title = `${ticketNumberWithPrefix}${separator}`;
  }
  title += ticketTitle;
  return title;
}

export function _ticketTitleWithPrefix(
  ticketNumberWithPrefix: any,
  ticketTitle: string,
) {
  return ticketTitleWithOptionalNumberPrefix(
    ticketNumberWithPrefix,
    ticketTitle,
    " | ",
  );
}

export function _ticketTitleWithPrefixWithHyphen(
  ticketNumberWithPrefix: any,
  ticketTitle: string,
) {
  return ticketTitleWithOptionalNumberPrefix(
    ticketNumberWithPrefix,
    ticketTitle,
    " - ",
  );
}

export function _truncatedTitle(ticketTitle: string) {
  if (ticketTitle.length > 80) {
    return ticketTitle.slice(0, 150) + "\u2026";
  }
  return ticketTitle;
}

export function _watcherList(watchers: any) {
  if (watchers != null && watchers != "") {
    return JSON.parse(watchers);
  }
  return [];
}

export function _secondaryContactList(secondaryContacts: any) {
  if (secondaryContacts != null && secondaryContacts != "") {
    return typeof secondaryContacts == "string"
      ? JSON.parse(secondaryContacts)
      : secondaryContacts;
  }
  return [];
}

export function _secondaryTechnicianList(secondaryTechnicians: any) {
  if (secondaryTechnicians != null && secondaryTechnicians != "") {
    return JSON.parse(secondaryTechnicians);
  }
}
